/**
 * components/email/DeliveryLog.tsx — recent mail-flow events for a domain.
 *
 * GET /delivery-log?lines=200|500 → newest first. Status pills use the
 * brand palette with the status word always printed; filter by status,
 * search by address, refresh, detail disclosure per row.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DeliveryEvent, fetchDeliveryLog, fmtAbsolute, fmtRelative, friendlyError } from './emailApi';

const STATUSES = ['sent', 'delivered', 'bounced', 'deferred', 'rejected'] as const;
type Known = typeof STATUSES[number];

const normStatus = (s: string | null | undefined): string => String(s || '').trim().toLowerCase();

const pillClass = (s: string) => {
  switch (s) {
    case 'delivered': return 'bg-secondary-500 border-secondary-500 text-primary-navy font-semibold';
    case 'sent': return 'bg-secondary-100 border-secondary-200 text-primary-navy';
    case 'deferred': return 'bg-white border-secondary-500 text-primary-navy';
    case 'bounced': return 'bg-secondary-200 border-primary-navy text-primary-navy font-semibold';
    case 'rejected': return 'bg-primary-navy border-primary-navy text-white font-semibold';
    default: return 'bg-gray-100 border-gray-200 text-gray-600';
  }
};
const pillIcon = (s: string) => {
  switch (s) {
    case 'delivered': return '✓';
    case 'sent': return '→';
    case 'deferred': return '⏳';
    case 'bounced': return '↩';
    case 'rejected': return '✕';
    default: return '·';
  }
};

export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = normStatus(status) || 'unknown';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${pillClass(s)}`}>
      <span aria-hidden>{pillIcon(s)}</span>{s}
    </span>
  );
};

export const DeliveryLog: React.FC<{ domainRowId: string }> = ({ domainRowId }) => {
  const [rows, setRows] = useState<DeliveryEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState<200 | 500>(200);
  const [status, setStatus] = useState<'all' | Known>('all');
  const [q, setQ] = useState('');
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDeliveryLog(domainRowId, lines);
      setRows(Array.isArray(data) ? data : []);
      setError(null);
      setFetchedAt(new Date());
    } catch (e: any) {
      setError(friendlyError(e, 'Couldn\'t load the delivery log'));
      setRows(prev => prev ?? []);
    } finally { setLoading(false); }
  }, [domainRowId, lines]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const list = rows || [];
    const needle = q.trim().toLowerCase();
    return list.filter(r => {
      if (status !== 'all' && normStatus(r.status) !== status) return false;
      if (needle && !`${r.from || ''} ${r.to || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [rows, status, q]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows || []) { const s = normStatus(r.status); c[s] = (c[s] || 0) + 1; }
    return c;
  }, [rows]);

  const toggle = (i: number) => setOpen(prev => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-primary-navy">Delivery log</h3>
          <p className="text-sm text-gray-600 mt-1">
            What happened to recent messages in and out of your domain. Newest first.
            {fetchedAt && <span className="text-gray-400"> Updated {fmtRelative(fetchedAt.toISOString())}.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="dl-lines" className="sr-only">Number of events</label>
          <select id="dl-lines" value={lines} onChange={e => setLines(Number(e.target.value) === 500 ? 500 : 200)}
            className="text-sm px-2 py-1.5 border border-gray-300 rounded-lg text-primary-navy bg-white focus:outline-none focus:ring-2 focus:ring-secondary-500">
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
          </select>
          <button type="button" onClick={load} disabled={loading} className="text-sm bg-white border border-gray-300 text-primary-navy px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-1">
          {(['all', ...STATUSES] as const).map(s => {
            const active = status === s;
            const n = s === 'all' ? (rows || []).length : (counts[s] || 0);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={active}
                onClick={() => setStatus(s)}
                className={`text-xs px-2.5 py-1 rounded-full border ${active ? 'bg-primary-navy text-white border-primary-navy' : 'bg-white text-primary-navy border-gray-300 hover:border-secondary-500'}`}
              >
                {s} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="dl-search" className="sr-only">Search by address</label>
          <input
            id="dl-search"
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search from / to address"
            className="w-full text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
          />
        </div>
      </div>

      {error && (
        <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-3">
          🚨 {error} <button type="button" onClick={load} className="underline ml-2">Retry</button>
        </div>
      )}

      {rows === null && !error && <div className="text-sm text-gray-500 py-4">Loading events…</div>}

      {rows && rows.length === 0 && !error && (
        <div className="text-sm text-gray-500 py-8 text-center">
          No delivery events yet. Send or receive a message and refresh.
        </div>
      )}

      {rows && rows.length > 0 && filtered.length === 0 && (
        <div className="text-sm text-gray-500 py-8 text-center">Nothing matches that filter.</div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th scope="col" className="py-2 pr-3 font-medium">When</th>
                <th scope="col" className="py-2 pr-3 font-medium">Status</th>
                <th scope="col" className="py-2 pr-3 font-medium">Direction</th>
                <th scope="col" className="py-2 pr-3 font-medium">From</th>
                <th scope="col" className="py-2 pr-3 font-medium">To</th>
                <th scope="col" className="py-2 font-medium"><span className="sr-only">Details</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r, i) => {
                const isOpen = open.has(i);
                const detailId = `dl-detail-${i}`;
                const hasDetail = !!(r.detail || r.relay || r.queue_id);
                return (
                  <React.Fragment key={`${r.queue_id || ''}-${String(r.ts)}-${i}`}>
                    <tr className="align-top">
                      <td className="py-2 pr-3 whitespace-nowrap text-primary-navy">
                        <div>{fmtRelative(r.ts, '—')}</div>
                        <div className="text-xs text-gray-500">{fmtAbsolute(r.ts)}</div>
                      </td>
                      <td className="py-2 pr-3"><StatusPill status={r.status} /></td>
                      <td className="py-2 pr-3 text-primary-navy/80 whitespace-nowrap">
                        {r.direction === 'inbound' ? '↓ in' : r.direction === 'outbound' ? '↑ out' : (r.direction || '—')}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-primary-navy break-all">{r.from || '—'}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-primary-navy break-all">{r.to || '—'}</td>
                      <td className="py-2 text-right">
                        {hasDetail && (
                          <button type="button" onClick={() => toggle(i)} aria-expanded={isOpen} aria-controls={detailId} className="text-xs text-primary-navy underline whitespace-nowrap">
                            {isOpen ? 'Hide' : 'Details'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && hasDetail && (
                      <tr id={detailId}>
                        <td colSpan={6} className="pb-3">
                          <dl className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs grid grid-cols-1 sm:grid-cols-[90px_1fr] gap-x-3 gap-y-1">
                            {r.queue_id && <><dt className="text-gray-500">Queue ID</dt><dd className="font-mono text-primary-navy break-all">{r.queue_id}</dd></>}
                            {r.relay && <><dt className="text-gray-500">Relay</dt><dd className="font-mono text-primary-navy break-all">{r.relay}</dd></>}
                            {r.detail && <><dt className="text-gray-500">Detail</dt><dd className="text-primary-navy break-words whitespace-pre-wrap">{r.detail}</dd></>}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeliveryLog;
